import { api } from "../lib/api";
import { sendToTerminal } from "../lib/terminalLogger";
import { TokenService } from "../lib/tokenService";
import {
	Campanha,
	Pergunta,
	IniciarResponse,
	RespostaPayload,
	RespostaResult,
	MeuResultado,
	RankingItem,
	CampanhaFilter,
	CreateCampanhaPayload,
	UpdateCampanhaPayload,
	CreatePerguntaPayload,
	UpdatePerguntaPayload,
	PaginatedResponse,
	EstatisticasCampanha,
	Premio,
	BancoPergunta,
	BancoPerguntaFilter,
	CreateBancoPerguntaPayload,
} from "../types/quiz";

// Rotas públicas - para todos os usuários autenticados
const PUBLIC_BASE_URL = "/campanhas";
// Rotas admin - apenas para administradores
const ADMIN_BASE_URL = "/admin/campanhas";
const LOG_PREFIX = "[QUIZ_SERVICE]";

/**
 * Normaliza uma campanha da API garantindo valores padrão para campos obrigatórios
 * Trata variações de nomenclatura entre frontend (snake_case pt) e backend (snake_case en)
 */
export function normalizeCampanha(campanha: any): Campanha {
	if (!campanha) return campanha;

	return {
		...campanha,
		// Campos básicos
		id: campanha.id,
		titulo: campanha.titulo || campanha.title || "",
		descricao: campanha.descricao || campanha.description || null,
		tipo: campanha.tipo || campanha.type || "quiz",

		// Período - aceitar ambos formatos
		data_inicio: campanha.data_inicio || campanha.start_date || null,
		data_fim: campanha.data_fim || campanha.end_date || null,

		// Configurações
		tempo_limite_pergunta:
			campanha.tempo_limite_pergunta ??
			campanha.time_limit_per_question ??
			null,
		mostrar_resultado_imediato:
			campanha.mostrar_resultado_imediato ??
			campanha.show_immediate_result ??
			false,
		mostrar_ranking: campanha.mostrar_ranking ?? campanha.show_ranking ?? false,
		embaralhar_perguntas:
			campanha.embaralhar_perguntas ?? campanha.shuffle_questions ?? false,
		embaralhar_opcoes:
			campanha.embaralhar_opcoes ?? campanha.shuffle_options ?? false,
		permite_refazer: campanha.permite_refazer ?? campanha.allow_retry ?? false,
		permite_pular: campanha.permite_pular ?? campanha.allow_skip ?? false,
		max_tentativas: campanha.max_tentativas ?? campanha.max_attempts ?? 1,

		// Gamificação
		pontos_por_acerto:
			campanha.pontos_por_acerto ?? campanha.points_per_correct ?? 10,
		pontos_bonus_tempo:
			campanha.pontos_bonus_tempo ?? campanha.time_bonus_points ?? 0,
		multiplicador_streak:
			campanha.multiplicador_streak ?? campanha.streak_multiplier ?? 0,

		// Prêmios
		premio_descricao:
			campanha.premio_descricao || campanha.prize_description || null,
		premios: campanha.premios || campanha.prizes || [],

		// Status e metadados
		ativa: campanha.ativa ?? campanha.active ?? true,
		total_perguntas: campanha.total_perguntas ?? campanha.questions_count ?? 0,
		total_participantes:
			campanha.total_participantes ?? campanha.participants_count ?? 0,

		// Data de criação - aceitar ambos formatos
		criada_em:
			campanha.criada_em || campanha.created_at || new Date().toISOString(),
	} as Campanha;
}

/**
 * Serviço para gerenciar campanhas e quiz
 * AGORA STATELESS - Cache gerenciado pelo React Query
 */
export const quizService = {
	// ============ Métodos Públicos (Usuário Comum) ============

	/**
	 * Lista campanhas disponíveis
	 */
	async getCampanhasDisponiveis(
		params?: CampanhaFilter,
	): Promise<{ success: true; data: PaginatedResponse<Campanha> }> {
		const token = TokenService.getAccessToken();
		if (!token) {
			sendToTerminal(
				`🚫 ${LOG_PREFIX} Tentativa de buscar campanhas sem token`,
			);
			throw new Error("Não autenticado - faça login antes de buscar campanhas");
		}

		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.tipo) queryParams.append("tipo", params.tipo);
		if (params?.ativa !== undefined)
			queryParams.append("ativa", params.ativa.toString());
		if (params?.data_inicio)
			queryParams.append("data_inicio", params.data_inicio);
		if (params?.data_fim) queryParams.append("data_fim", params.data_fim);

		const paramsString = queryParams.toString();

		try {
			sendToTerminal(`📤 ${LOG_PREFIX} Buscando campanhas disponíveis`, {
				params: paramsString || "(sem filtros)",
			});

			const url = `${PUBLIC_BASE_URL}${paramsString ? `?${paramsString}` : ""}`;
			const response = await api(url, { method: "GET" });

			let campanhasArray: any[] = [];
			let paginationData: any = null;

			if (response?.data?.data) {
				campanhasArray = Array.isArray(response.data.data)
					? response.data.data
					: [];
				paginationData = response.data.pagination;
			} else if (Array.isArray(response)) {
				campanhasArray = response;
			} else if (response?.data) {
				if (Array.isArray(response.data)) {
					campanhasArray = response.data;
					paginationData = response.pagination;
				} else if (response.data?.campanhas) {
					campanhasArray = response.data.campanhas;
					paginationData = response.data.pagination;
				}
			} else if (response?.campanhas) {
				campanhasArray = response.campanhas;
				paginationData = response.pagination;
			}

			const normalizedData: PaginatedResponse<Campanha> = {
				data: campanhasArray.map(normalizeCampanha),
				pagination: paginationData || {
					page: 1,
					limit: 10,
					total: campanhasArray.length,
					totalPages: 1,
				},
			};

			return { success: true, data: normalizedData };
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao buscar campanhas`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Obtém detalhes de uma campanha
	 */
	async getCampanha(campanhaId: number): Promise<Campanha> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(`📤 ${LOG_PREFIX} Buscando campanha ${campanhaId}`);
			const response = await api(`${PUBLIC_BASE_URL}/${campanhaId}`, {
				method: "GET",
			});

			const campanhaData = response?.data?.data || response?.data || response;

			if (!campanhaData || typeof campanhaData !== "object") {
				throw new Error("Resposta inválida da API - campanha não encontrada");
			}

			return normalizeCampanha(campanhaData);
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao buscar campanha ${campanhaId}`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Inicia participação em uma campanha
	 */
	async iniciarParticipacao(campanhaId: number): Promise<IniciarResponse> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`🚀 ${LOG_PREFIX} Iniciando participação na campanha ${campanhaId}`,
			);
			const response = await api(`${PUBLIC_BASE_URL}/${campanhaId}/iniciar`, {
				method: "POST",
			});

			const data = response?.data?.data || response?.data || response;
			sendToTerminal(`✅ ${LOG_PREFIX} Participação iniciada`, {
				rankingId: data.ranking_id,
				tentativa: data.tentativa,
			});
			return data;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao iniciar participação`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Envia resposta de uma pergunta
	 */
	async responder(
		campanhaId: number,
		resposta: RespostaPayload,
	): Promise<RespostaResult> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(`📝 ${LOG_PREFIX} Enviando resposta`, {
				campanhaId,
				perguntaId: resposta.pergunta_id,
			});
			const response = await api(`${PUBLIC_BASE_URL}/${campanhaId}/responder`, {
				method: "POST",
				body: JSON.stringify(resposta),
			});

			const data = response?.data?.data || response?.data || response;
			sendToTerminal(`✅ ${LOG_PREFIX} Resposta registrada`, {
				pontos: data.pontos_obtidos,
				correta: data.correta,
			});
			return data;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao enviar resposta`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Finaliza participação em uma campanha
	 */
	async finalizar(campanhaId: number): Promise<any> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`🏁 ${LOG_PREFIX} Finalizando participação na campanha ${campanhaId}`,
			);
			const response = await api(`${PUBLIC_BASE_URL}/${campanhaId}/finalizar`, {
				method: "POST",
			});

			const data = response?.data?.data || response?.data || response;
			sendToTerminal(`✅ ${LOG_PREFIX} Participação finalizada`, {
				pontosTotal: data.pontos_total,
				posicaoFinal: data.posicao_final,
			});
			return data;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao finalizar participação`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Obtém meu resultado na campanha
	 */
	async getMeuResultado(campanhaId: number): Promise<MeuResultado> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`📊 ${LOG_PREFIX} Buscando resultado da campanha ${campanhaId}`,
			);
			const response = await api(
				`${PUBLIC_BASE_URL}/${campanhaId}/meu-resultado`,
				{
					method: "GET",
				},
			);

			const resultData = response?.data?.data || response?.data || response;
			return resultData;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao buscar resultado`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Obtém ranking da campanha
	 */
	async getRanking(
		campanhaId: number,
		limit = 10,
	): Promise<{ ranking: RankingItem[]; minha_posicao?: number }> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`📊 ${LOG_PREFIX} Buscando ranking da campanha ${campanhaId}`,
			);
			const response = await api(
				`${PUBLIC_BASE_URL}/${campanhaId}/ranking?limit=${limit}`,
				{
					method: "GET",
				},
			);

			const data = response?.data?.data || response?.data || response;
			return data;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao buscar ranking`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	// ============ Métodos Admin ============

	/**
	 * Lista todas as campanhas (admin)
	 */
	async getAllCampanhas(
		params?: CampanhaFilter,
	): Promise<{ success: true; data: PaginatedResponse<Campanha> }> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.tipo) queryParams.append("tipo", params.tipo);
		if (params?.ativa !== undefined)
			queryParams.append("ativa", params.ativa.toString());

		const paramsString = queryParams.toString();

		try {
			sendToTerminal(`📤 ${LOG_PREFIX} [ADMIN] Buscando todas as campanhas`);
			const url = `${ADMIN_BASE_URL}${paramsString ? `?${paramsString}` : ""}`;
			const response = await api(url, { method: "GET" });

			let campanhasArray: any[] = [];
			let paginationData: any = null;

			if (response?.data?.data) {
				campanhasArray = Array.isArray(response.data.data)
					? response.data.data
					: [];
				paginationData = response.data.pagination;
			} else if (Array.isArray(response)) {
				campanhasArray = response;
			} else if (response?.data) {
				if (Array.isArray(response.data)) {
					campanhasArray = response.data;
					paginationData = response.pagination;
				} else if (response.data?.campanhas) {
					campanhasArray = response.data.campanhas;
					paginationData = response.data.pagination;
				}
			} else if (response?.campanhas) {
				campanhasArray = response.campanhas;
				paginationData = response.pagination;
			}

			const normalizedData: PaginatedResponse<Campanha> = {
				data: campanhasArray.map(normalizeCampanha),
				pagination: paginationData || {
					page: 1,
					limit: 10,
					total: campanhasArray.length,
					totalPages: 1,
				},
			};

			return { success: true, data: normalizedData };
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao buscar campanhas`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Cria uma nova campanha (admin)
	 */
	async createCampanha(data: CreateCampanhaPayload): Promise<Campanha> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(`➕ ${LOG_PREFIX} [ADMIN] Criando campanha`, {
				titulo: data.titulo,
				tipo: data.tipo,
			});
			const response = await api(ADMIN_BASE_URL, {
				method: "POST",
				body: JSON.stringify(data),
			});

			const campanhaData = response?.data?.data || response?.data || response;
			const campanha = normalizeCampanha(campanhaData);
			sendToTerminal(`✅ ${LOG_PREFIX} [ADMIN] Campanha criada`, {
				id: campanha.id,
			});
			return campanha;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao criar campanha`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Atualiza uma campanha (admin)
	 */
	async updateCampanha(
		id: number,
		data: UpdateCampanhaPayload,
	): Promise<Campanha> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(`✏️ ${LOG_PREFIX} [ADMIN] Atualizando campanha ${id}`);
			const response = await api(`${ADMIN_BASE_URL}/${id}`, {
				method: "PUT",
				body: JSON.stringify(data),
			});

			const campanhaData = response?.data?.data || response?.data || response;
			const campanha = normalizeCampanha(campanhaData);
			sendToTerminal(`✅ ${LOG_PREFIX} [ADMIN] Campanha atualizada`);
			return campanha;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao atualizar campanha`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Exclui uma campanha (admin)
	 */
	async deleteCampanha(id: number): Promise<void> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(`🗑️ ${LOG_PREFIX} [ADMIN] Excluindo campanha ${id}`);
			await api(`${ADMIN_BASE_URL}/${id}`, {
				method: "DELETE",
			});

			sendToTerminal(`✅ ${LOG_PREFIX} [ADMIN] Campanha excluída`);
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao excluir campanha`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Lista perguntas de uma campanha (admin)
	 */
	async getPerguntas(campanhaId: number): Promise<Pergunta[]> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`📋 ${LOG_PREFIX} [ADMIN] Buscando perguntas da campanha ${campanhaId}`,
			);
			const response = await api(`${ADMIN_BASE_URL}/${campanhaId}/perguntas`, {
				method: "GET",
			});

			return (
				response?.data?.data ||
				response?.data?.ranking ||
				response?.data ||
				response ||
				[]
			);
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao buscar perguntas`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Cria uma pergunta (admin)
	 */
	async createPergunta(
		campanhaId: number,
		data: CreatePerguntaPayload,
	): Promise<Pergunta> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`➕ ${LOG_PREFIX} [ADMIN] Criando pergunta na campanha ${campanhaId}`,
			);
			const response = await api(`${ADMIN_BASE_URL}/${campanhaId}/perguntas`, {
				method: "POST",
				body: JSON.stringify(data),
			});

			const pergunta = response?.data?.data || response?.data || response;
			sendToTerminal(`✅ ${LOG_PREFIX} [ADMIN] Pergunta criada`, {
				id: pergunta.id,
			});
			return pergunta;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao criar pergunta`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Atualiza uma pergunta (admin)
	 */
	async updatePergunta(
		campanhaId: number,
		perguntaId: number,
		data: UpdatePerguntaPayload,
	): Promise<Pergunta> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`✏️ ${LOG_PREFIX} [ADMIN] Atualizando pergunta ${perguntaId}`,
			);
			const response = await api(
				`${ADMIN_BASE_URL}/${campanhaId}/perguntas/${perguntaId}`,
				{
					method: "PUT",
					body: JSON.stringify(data),
				},
			);

			const pergunta = response?.data?.data || response?.data || response;
			sendToTerminal(`✅ ${LOG_PREFIX} [ADMIN] Pergunta atualizada`);
			return pergunta;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao atualizar pergunta`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Exclui uma pergunta (admin)
	 */
	async deletePergunta(campanhaId: number, perguntaId: number): Promise<void> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`🗑️ ${LOG_PREFIX} [ADMIN] Excluindo pergunta ${perguntaId}`,
			);
			await api(`${ADMIN_BASE_URL}/${campanhaId}/perguntas/${perguntaId}`, {
				method: "DELETE",
			});

			sendToTerminal(`✅ ${LOG_PREFIX} [ADMIN] Pergunta excluída`);
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao excluir pergunta`, {
				error: error?.message || String(error),
				status: error?.status,
			});
			throw error;
		}
	},

	/**
	 * Obtém ranking completo (admin)
	 */
	async getRankingCompleto(
		campanhaId: number,
		limit = 50,
	): Promise<RankingItem[]> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`📊 ${LOG_PREFIX} [ADMIN] Buscando ranking completo ${campanhaId}`,
			);
			const response = await api(
				`${ADMIN_BASE_URL}/${campanhaId}/ranking?limit=${limit}`,
				{
					method: "GET",
				},
			);

			return response?.data?.data || response?.data || response || [];
		} catch (error: any) {
			sendToTerminal(
				`❌ ${LOG_PREFIX} [ADMIN] Erro ao buscar ranking completo`,
				{
					error: error?.message || String(error),
					status: error?.status,
				},
			);
			throw error;
		}
	},

	/**
	 * Obtém estatísticas de uma campanha (admin)
	 */
	async getEstatisticas(campanhaId: number): Promise<EstatisticasCampanha> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`📊 ${LOG_PREFIX} [ADMIN] Buscando estatísticas ${campanhaId}`,
			);
			const response = await api(
				`${ADMIN_BASE_URL}/${campanhaId}/estatisticas`,
				{ method: "GET" },
			);
			return response?.data?.data || response?.data || response;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao buscar estatísticas`, {
				error: error?.message || String(error),
			});
			throw error;
		}
	},

	/**
	 * Exporta resultados em CSV (admin)
	 */
	async exportarResultados(
		campanhaId: number,
	): Promise<{ filename: string; csv: string }> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`📥 ${LOG_PREFIX} [ADMIN] Exportando resultados ${campanhaId}`,
			);
			const response = await api(`${ADMIN_BASE_URL}/${campanhaId}/export`, {
				method: "GET",
			});
			return response?.data?.data || response?.data || response;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} [ADMIN] Erro ao exportar resultados`, {
				error: error?.message || String(error),
			});
			throw error;
		}
	},

	// ============ Prêmios Admin ============

	async getPremios(campanhaId: number): Promise<Premio[]> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		const response = await api(`${ADMIN_BASE_URL}/${campanhaId}/premios`, {
			method: "GET",
		});
		return response?.data?.data || response?.data || response || [];
	},

	async createPremio(
		campanhaId: number,
		data: Omit<Premio, "id">,
	): Promise<Premio> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		const response = await api(`${ADMIN_BASE_URL}/${campanhaId}/premios`, {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response?.data?.data || response?.data || response;
	},

	async deletePremio(campanhaId: number, premioId: number): Promise<void> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		await api(`${ADMIN_BASE_URL}/${campanhaId}/premios/${premioId}`, {
			method: "DELETE",
		});
	},

	// ============ Banco de Perguntas Admin ============

	async getBancoPerguntas(
		params?: BancoPerguntaFilter,
	): Promise<PaginatedResponse<BancoPergunta> & { categorias: string[] }> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		const queryParams = new URLSearchParams();
		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.categoria) queryParams.append("categoria", params.categoria);
		if (params?.dificuldade)
			queryParams.append("dificuldade", params.dificuldade);
		if (params?.tipo) queryParams.append("tipo", params.tipo);

		const qs = queryParams.toString();
		const response = await api(`/admin/banco-perguntas${qs ? `?${qs}` : ""}`, {
			method: "GET",
		});
		return response?.data?.data || response?.data || response;
	},

	async createBancoPergunta(
		data: CreateBancoPerguntaPayload,
	): Promise<BancoPergunta> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		const response = await api("/admin/banco-perguntas", {
			method: "POST",
			body: JSON.stringify(data),
		});
		return response?.data?.data || response?.data || response;
	},

	async importarDoBanco(
		campanhaId: number,
		bancoPerguntaId: number,
		ordem?: number,
	): Promise<Pergunta> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		const response = await api(
			`${ADMIN_BASE_URL}/${campanhaId}/perguntas/importar`,
			{
				method: "POST",
				body: JSON.stringify({ banco_pergunta_id: bancoPerguntaId, ordem }),
			},
		);
		return response?.data?.data || response?.data || response;
	},

	// ============ Público: Pular Pergunta ============

	async pular(
		campanhaId: number,
		perguntaId: number,
	): Promise<{ skipped: boolean }> {
		const token = TokenService.getAccessToken();
		if (!token) throw new Error("Não autenticado");

		try {
			sendToTerminal(
				`⏭️ ${LOG_PREFIX} Pulando pergunta ${perguntaId} na campanha ${campanhaId}`,
			);
			const response = await api(`${PUBLIC_BASE_URL}/${campanhaId}/pular`, {
				method: "POST",
				body: JSON.stringify({ pergunta_id: perguntaId }),
			});
			return response?.data?.data || response?.data || response;
		} catch (error: any) {
			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao pular pergunta`, {
				error: error?.message || String(error),
			});
			throw error;
		}
	},
};
